import { useEffect, useMemo, useState } from 'react';
import { formatDisplayDateTime } from '../../shared/utils/dateFormat';
import { brokerAdminRequest } from './brokerAdminApi';
import './BrokerAdminPage.css';

const SESSION_KEY = 'mqtt-monitor-broker-admin';
const ACL_TYPES = [
  ['publishClientSend', 'Publish'],
  ['publishClientReceive', 'Receive published messages'],
  ['subscribePattern', 'Subscribe (pattern)'],
  ['subscribeLiteral', 'Subscribe (literal)'],
  ['unsubscribePattern', 'Unsubscribe (pattern)'],
  ['unsubscribeLiteral', 'Unsubscribe (literal)'],
];

function readCredentials() {
  try {
    return JSON.parse(sessionStorage.getItem(SESSION_KEY)) || null;
  } catch {
    return null;
  }
}

function roleNamesFor(account) {
  return account.roles.map((role) => role.roleName);
}

function getSelectedValues(event) {
  return Array.from(event.target.selectedOptions, (option) => option.value);
}

function formatAuditDetails(detailsJson) {
  try {
    return JSON.stringify(JSON.parse(detailsJson), null, 2);
  } catch {
    return detailsJson;
  }
}

export default function BrokerAdminPage() {
  const [credentials, setCredentials] = useState(readCredentials);
  const [actor, setActor] = useState(credentials?.actor || 'test-admin');
  const [apiKey, setApiKey] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [roles, setRoles] = useState([]);
  const [auditEvents, setAuditEvents] = useState([]);
  const [roleEdits, setRoleEdits] = useState({});
  const [selectedRoleName, setSelectedRoleName] = useState('');
  const [aclDraft, setAclDraft] = useState([]);
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [newAccount, setNewAccount] = useState({
    username: '', password: '', clientId: '', roleNames: [],
  });
  const [newRoleName, setNewRoleName] = useState('');
  const [passwordTarget, setPasswordTarget] = useState('');
  const [replacementPassword, setReplacementPassword] = useState('');

  const editableRoles = useMemo(() => roles.filter((role) => !role.isProtected), [roles]);
  const selectedRole = roles.find((role) => role.roleName === selectedRoleName);

  async function loadAll(activeCredentials = credentials) {
    if (!activeCredentials) return;
    setBusy(true);
    setFeedback(null);
    try {
      const [nextAccounts, nextRoles, nextAuditEvents] = await Promise.all([
        brokerAdminRequest('/accounts', activeCredentials),
        brokerAdminRequest('/roles', activeCredentials),
        brokerAdminRequest('/audit?limit=100', activeCredentials),
      ]);
      setAccounts(nextAccounts);
      setRoles(nextRoles);
      setAuditEvents(nextAuditEvents);
      setRoleEdits(Object.fromEntries(
        nextAccounts.map((account) => [account.username, roleNamesFor(account)]),
      ));
      setSelectedRoleName((current) => (
        nextRoles.some((role) => role.roleName === current && !role.isProtected)
          ? current
          : nextRoles.find((role) => !role.isProtected)?.roleName || ''
      ));
    } catch (error) {
      setFeedback({ type: 'error', message: error.message });
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    if (credentials) loadAll(credentials);
    // Credentials change only when an administrator connects or disconnects.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [credentials]);

  useEffect(() => {
    const role = roles.find((item) => item.roleName === selectedRoleName);
    setAclDraft(role ? role.acls.map((acl) => ({ ...acl })) : []);
  }, [roles, selectedRoleName]);

  function connect(event) {
    event.preventDefault();
    const nextCredentials = { actor: actor.trim() || 'test-admin', apiKey };
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(nextCredentials));
    setCredentials(nextCredentials);
    setApiKey('');
  }

  function disconnect() {
    sessionStorage.removeItem(SESSION_KEY);
    setCredentials(null);
    setAccounts([]);
    setRoles([]);
    setAuditEvents([]);
    setFeedback(null);
  }

  async function mutate(successMessage, callback) {
    setBusy(true);
    setFeedback(null);
    try {
      await callback();
      await loadAll();
      setFeedback({ type: 'success', message: successMessage });
    } catch (error) {
      setFeedback({ type: 'error', message: error.message });
      setBusy(false);
    }
  }

  function toggleNewAccountRole(roleName) {
    setNewAccount((current) => ({
      ...current,
      roleNames: current.roleNames.includes(roleName)
        ? current.roleNames.filter((item) => item !== roleName)
        : [...current.roleNames, roleName],
    }));
  }

  function createAccount(event) {
    event.preventDefault();
    mutate(`Created device account ${newAccount.username}.`, async () => {
      await brokerAdminRequest('/accounts', credentials, {
        method: 'POST',
        body: JSON.stringify({
          ...newAccount,
          clientId: newAccount.clientId || null,
        }),
      });
      setNewAccount({ username: '', password: '', clientId: '', roleNames: [] });
    });
  }

  function setAccountStatus(account) {
    mutate(
      `${account.disabled ? 'Enabled' : 'Disabled'} ${account.username}.`,
      () => brokerAdminRequest(
        `/accounts/${encodeURIComponent(account.username)}/status`,
        credentials,
        { method: 'PATCH', body: JSON.stringify({ disabled: !account.disabled }) },
      ),
    );
  }

  function saveAccountRoles(account) {
    mutate(
      `Updated roles for ${account.username}.`,
      () => brokerAdminRequest(
        `/accounts/${encodeURIComponent(account.username)}/roles`,
        credentials,
        {
          method: 'PUT',
          body: JSON.stringify({ roleNames: roleEdits[account.username] || [] }),
        },
      ),
    );
  }

  function resetPassword(event) {
    event.preventDefault();
    const target = passwordTarget;
    mutate(`Reset the password for ${target}.`, async () => {
      await brokerAdminRequest(
        `/accounts/${encodeURIComponent(target)}/password`,
        credentials,
        { method: 'POST', body: JSON.stringify({ password: replacementPassword }) },
      );
      setPasswordTarget('');
      setReplacementPassword('');
    });
  }

  function createRole(event) {
    event.preventDefault();
    const roleName = newRoleName;
    mutate(`Created role ${roleName}.`, async () => {
      await brokerAdminRequest('/roles', credentials, {
        method: 'POST', body: JSON.stringify({ roleName }),
      });
      setNewRoleName('');
      setSelectedRoleName(roleName);
    });
  }

  function updateAcl(index, property, value) {
    setAclDraft((current) => current.map((acl, aclIndex) => (
      aclIndex === index ? { ...acl, [property]: value } : acl
    )));
  }

  function saveAcls() {
    mutate(
      `Updated topic access for ${selectedRoleName}.`,
      () => brokerAdminRequest(
        `/roles/${encodeURIComponent(selectedRoleName)}/acls`,
        credentials,
        { method: 'PUT', body: JSON.stringify({ acls: aclDraft }) },
      ),
    );
  }

  if (!credentials) {
    return (
      <section className="admin-page admin-login-page">
        <form className="admin-login-card" onSubmit={connect}>
          <p className="admin-eyebrow">Restricted broker administration</p>
          <h1>Manage GPS device access</h1>
          <p>
            Enter the separate administration API key. Broker admin credentials remain on the server
            and are never sent to this browser.
          </p>
          <label className="admin-field">
            <span>Audit actor</span>
            <input value={actor} onChange={(event) => setActor(event.target.value)} required />
          </label>
          <label className="admin-field">
            <span>Administration API key</span>
            <input
              type="password"
              value={apiKey}
              onChange={(event) => setApiKey(event.target.value)}
              minLength={20}
              autoComplete="current-password"
              required
            />
          </label>
          <button className="admin-primary" type="submit">Open device access</button>
        </form>
      </section>
    );
  }

  return (
    <section className="admin-page">
      <div className="admin-hero">
        <div>
          <p className="admin-eyebrow">Mosquitto Dynamic Security</p>
          <h1>GPS device accounts and topic access</h1>
          <p>Issue individual device credentials, assign reusable roles, and audit every change.</p>
        </div>
        <div className="admin-hero-actions">
          <span>Actor: {credentials.actor}</span>
          <button type="button" className="admin-secondary" onClick={() => loadAll()} disabled={busy}>
            Refresh
          </button>
          <button type="button" className="admin-secondary" onClick={disconnect}>Lock</button>
        </div>
      </div>

      {feedback ? (
        <div className={`admin-banner admin-banner-${feedback.type}`} role="status">
          {feedback.message}
        </div>
      ) : null}

      <div className="admin-summary">
        <div><strong>{accounts.length}</strong><span>Broker accounts</span></div>
        <div><strong>{roles.length}</strong><span>Roles</span></div>
        <div><strong>{auditEvents.length}</strong><span>Recent audit events</span></div>
      </div>

      <div className="admin-layout">
        <form className="admin-card admin-create-card" onSubmit={createAccount}>
          <div className="admin-card-heading">
            <div><h2>Create GPS device account</h2><p>Use unique credentials for every physical box.</p></div>
          </div>
          <label className="admin-field">
            <span>Username</span>
            <input
              value={newAccount.username}
              onChange={(event) => setNewAccount({ ...newAccount, username: event.target.value })}
              placeholder="tracker-001"
              maxLength={128}
              required
            />
          </label>
          <label className="admin-field">
            <span>MQTT client ID (optional lock)</span>
            <input
              value={newAccount.clientId}
              onChange={(event) => setNewAccount({ ...newAccount, clientId: event.target.value })}
              placeholder="gps-box-serial-001"
              maxLength={128}
            />
          </label>
          <label className="admin-field">
            <span>Initial password</span>
            <input
              type="password"
              value={newAccount.password}
              onChange={(event) => setNewAccount({ ...newAccount, password: event.target.value })}
              minLength={12}
              autoComplete="new-password"
              required
            />
          </label>
          <fieldset className="admin-role-picker">
            <legend>Initial roles</legend>
            {editableRoles.length === 0 ? <p>Create a device role first.</p> : editableRoles.map((role) => (
              <label key={role.roleName}>
                <input
                  type="checkbox"
                  checked={newAccount.roleNames.includes(role.roleName)}
                  onChange={() => toggleNewAccountRole(role.roleName)}
                />
                {role.roleName}
              </label>
            ))}
          </fieldset>
          <button className="admin-primary" type="submit" disabled={busy}>Create account</button>
        </form>

        <div className="admin-card admin-accounts-card">
          <div className="admin-card-heading">
            <div><h2>Device accounts</h2><p>Disable access immediately or rotate a device password.</p></div>
          </div>
          {accounts.length === 0 ? (
            <p className="admin-empty">No accounts returned by the broker.</p>
          ) : (
            <div className="admin-account-list">
              {accounts.map((account) => (
                <article className="admin-account" key={account.username}>
                  <div className="admin-account-title">
                    <div>
                      <strong>{account.username}</strong>
                      <span>{account.clientId || 'Any MQTT client ID'}</span>
                    </div>
                    <span className={`admin-state ${account.disabled ? 'disabled' : 'enabled'}`}>
                      {account.disabled ? 'Disabled' : 'Enabled'}
                    </span>
                  </div>
                  <label className="admin-field admin-compact-field">
                    <span>Assigned roles</span>
                    <select
                      multiple
                      value={roleEdits[account.username] || []}
                      onChange={(event) => setRoleEdits({
                        ...roleEdits,
                        [account.username]: getSelectedValues(event),
                      })}
                      disabled={account.isProtected}
                    >
                      {(account.isProtected ? roles : editableRoles)
                        .map((role) => <option key={role.roleName}>{role.roleName}</option>)}
                    </select>
                  </label>
                  <div className="admin-inline-actions">
                    <button
                      type="button"
                      className="admin-secondary"
                      onClick={() => saveAccountRoles(account)}
                      disabled={busy || account.isProtected}
                    >
                      Save roles
                    </button>
                    <button
                      type="button"
                      className={account.disabled ? 'admin-enable' : 'admin-danger'}
                      onClick={() => setAccountStatus(account)}
                      disabled={busy || account.isProtected}
                    >
                      {account.disabled ? 'Enable' : 'Disable'}
                    </button>
                    <button
                      type="button"
                      className="admin-secondary"
                      onClick={() => {
                        setPasswordTarget(account.username);
                        setReplacementPassword('');
                      }}
                      disabled={busy || account.isProtected}
                    >
                      Reset password
                    </button>
                  </div>
                  {account.isProtected ? <p className="admin-protected">Protected system account</p> : null}
                  {passwordTarget === account.username ? (
                    <form className="admin-reset-form" onSubmit={resetPassword}>
                      <input
                        type="password"
                        value={replacementPassword}
                        onChange={(event) => setReplacementPassword(event.target.value)}
                        placeholder="New password (12+ characters)"
                        minLength={12}
                        autoComplete="new-password"
                        required
                      />
                      <button className="admin-primary" type="submit">Apply reset</button>
                      <button type="button" className="admin-secondary" onClick={() => setPasswordTarget('')}>
                        Cancel
                      </button>
                    </form>
                  ) : null}
                </article>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="admin-layout admin-role-layout">
        <form className="admin-card" onSubmit={createRole}>
          <div className="admin-card-heading">
            <div><h2>Create device role</h2><p>Roles group topic permissions for one or many GPS boxes.</p></div>
          </div>
          <label className="admin-field">
            <span>Role name</span>
            <input
              value={newRoleName}
              onChange={(event) => setNewRoleName(event.target.value)}
              placeholder="gps-device"
              maxLength={128}
              required
            />
          </label>
          <button className="admin-primary" type="submit" disabled={busy}>Create role</button>
        </form>

        <div className="admin-card admin-acl-card">
          <div className="admin-card-heading">
            <div><h2>Publish and subscribe access</h2><p>ACL priority is evaluated from the highest number down.</p></div>
          </div>
          <label className="admin-field">
            <span>Role to edit</span>
            <select value={selectedRoleName} onChange={(event) => setSelectedRoleName(event.target.value)}>
              <option value="">Select a role</option>
              {editableRoles.map((role) => <option key={role.roleName}>{role.roleName}</option>)}
            </select>
          </label>
          {selectedRole ? (
            <>
              <div className="admin-acl-list">
                {aclDraft.map((acl, index) => (
                  <div className="admin-acl-row" key={`${acl.aclType}-${acl.topic}-${index}`}>
                    <select value={acl.aclType} onChange={(event) => updateAcl(index, 'aclType', event.target.value)}>
                      {ACL_TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                    </select>
                    <input
                      value={acl.topic}
                      onChange={(event) => updateAcl(index, 'topic', event.target.value)}
                      placeholder="device/%u/gps"
                    />
                    <select
                      value={acl.allow ? 'allow' : 'deny'}
                      onChange={(event) => updateAcl(index, 'allow', event.target.value === 'allow')}
                    >
                      <option value="allow">Allow</option>
                      <option value="deny">Deny</option>
                    </select>
                    <input
                      type="number"
                      value={acl.priority}
                      min="-100000"
                      max="100000"
                      onChange={(event) => updateAcl(index, 'priority', Number(event.target.value))}
                      aria-label="ACL priority"
                    />
                    <button
                      type="button"
                      className="admin-danger"
                      onClick={() => setAclDraft((current) => current.filter((_, aclIndex) => aclIndex !== index))}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
              <div className="admin-inline-actions">
                <button
                  type="button"
                  className="admin-secondary"
                  onClick={() => setAclDraft((current) => [...current, {
                    aclType: 'publishClientSend', topic: 'device/%u/gps', allow: true, priority: 10,
                  }])}
                >
                  Add ACL
                </button>
                <button type="button" className="admin-primary" onClick={saveAcls} disabled={busy}>
                  Save topic access
                </button>
              </div>
            </>
          ) : <p className="admin-empty">Create or select a non-system role to configure ACLs.</p>}
        </div>
      </div>

      <div className="admin-card admin-audit-card">
        <div className="admin-card-heading">
          <div><h2>Account and ACL audit history</h2><p>Passwords are never included in audit details.</p></div>
        </div>
        {auditEvents.length === 0 ? <p className="admin-empty">No administration changes recorded yet.</p> : (
          <div className="admin-table-wrap">
            <table className="admin-audit-table">
              <thead><tr><th>Time</th><th>Actor</th><th>Action</th><th>Target</th><th>Result</th><th>Details</th></tr></thead>
              <tbody>
                {auditEvents.map((event) => (
                  <tr key={event.id}>
                    <td>{formatDisplayDateTime(event.occurredAt)}</td>
                    <td>{event.actor}<small>{event.remoteAddress || ''}</small></td>
                    <td>{event.action}</td>
                    <td>{event.targetIdentifier}<small>{event.targetType}</small></td>
                    <td><span className={`admin-result ${event.succeeded ? 'success' : 'failed'}`}>
                      {event.succeeded ? 'Success' : 'Failed'}
                    </span></td>
                    <td><pre>{event.errorMessage || formatAuditDetails(event.detailsJson)}</pre></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
